import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../utils/firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { activityFieldTypes, displayActivityName, validateActivity, formatActivityDetails } from '../utils/activities';
import { calorieBurnRates, calculateCaloriesBurned } from '../utils/calories';
import logger from '../utils/logger';
import { toMillis } from '../utils/timestamps';
import { getDateLabel, getDateKey } from '../utils/dateDivider';
import { calculateStreak } from '../utils/streaks';
import CaloriesInfoPopup from './CaloriesInfoPopup';
import ModalBackdrop from './ModalBackdrop';

function ActivityLog({ userId, currentWeight, goal, caloriesConsumed = 0, onCaloriesBurned, onActivityLogged, onGoalUpdate, onError }) {
  const [activities, setActivities] = useState([]);
  const formRef = useRef(null);
  const [availableActivities, setAvailableActivities] = useState([]);
  const [activityPrefs, setActivityPrefs] = useState({});
  const [activityLocation, setActivityLocation] = useState(() => {
    return localStorage.getItem('activityLocation') || 'gym';
  });
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [editActivity, setEditActivity] = useState(null);
  const [newActivity, setNewActivity] = useState({ type: '', reps: '', time: '', weightLifted: '' });
  const [showAllActivitiesPopup, setShowAllActivitiesPopup] = useState(false);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [todayProgress, setTodayProgress] = useState(0);
  const [showGoalEditor, setShowGoalEditor] = useState(false);
  const [editGoal, setEditGoal] = useState({ target: '', type: 'reps' });

  // Streak calculation runs client-side from already-loaded activities.
  // Recomputes whenever activities change (optimistic inserts included).
  const { currentStreak, longestStreak } = useMemo(
    () => calculateStreak(activities),
    [activities]
  );

  // Activities filtered by the current Home/Gym toggle selection.
  const filteredActivities = useMemo(
    () => availableActivities.filter(name => activityPrefs[name]?.[activityLocation]),
    [availableActivities, activityPrefs, activityLocation]
  );

  const handleLocationToggle = (loc) => {
    setActivityLocation(loc);
    localStorage.setItem('activityLocation', loc);
  };

  // Effect #3: fetchAvailableActivities — reads users/{uid}/preferences/activities,
  // initializes it as empty if missing, normalizes and dedupes display names.
  // Stores per-activity { home, gym } prefs for location-based filtering.
  useEffect(() => {
    if (!userId) return;

    const fetchAvailableActivities = async () => {
      try {
        const activitiesDoc = await getDoc(doc(db, 'users', userId, 'preferences', 'activities'));
        const prefs = {};
        if (activitiesDoc.exists()) {
          const savedData = activitiesDoc.data().activities;
          if (Array.isArray(savedData)) {
            // Legacy format: array of name strings — treat all as home+gym
            savedData.forEach(name => {
              prefs[displayActivityName(name.trim())] = { home: true, gym: true };
            });
          } else if (savedData && typeof savedData === 'object') {
            // New format: object keyed by name → { home, gym } or boolean
            Object.entries(savedData).forEach(([name, val]) => {
              const displayName = displayActivityName(name.trim());
              if (typeof val === 'boolean') {
                prefs[displayName] = val ? { home: true, gym: true } : { home: false, gym: false };
              } else {
                prefs[displayName] = { home: !!val.home, gym: !!val.gym };
              }
            });
          }
        } else {
          await setDoc(doc(db, 'users', userId, 'preferences', 'activities'), { activities: {} });
        }
        setActivityPrefs(prefs);
        const allNames = Object.keys(prefs).filter(
          name => prefs[name].home || prefs[name].gym
        );
        const sortedActivities = [...new Set(allNames)].sort();
        setAvailableActivities(sortedActivities);
      } catch (error) {
        logger.error(error, { page: 'User', action: 'fetchAvailableActivities' });
        onError('Failed to fetch available activities. Please try again.');
      }
    };

    fetchAvailableActivities();
  }, [userId, onError]);

  // Effect #4: fetchActivities — reads users/{uid}/reps. The old read-then-write
  // activityType case-normalization loop was removed; new writes already
  // normalize via handleActivitySubmit/handleEditSubmit, and a one-time
  // migration is available as "Normalize Activity Types" on the account page.
  // Display normalization via displayActivityName() still happens in-memory
  // below so legacy records render correctly until migrated.
  useEffect(() => {
    if (!userId) return;

    const fetchActivities = async () => {
      setLoadingActivities(true);
      try {
        const querySnapshot = await getDocs(collection(db, 'users', userId, 'reps'));
        let activityData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        activityData = activityData.map(activity => ({
          ...activity,
          activityType: displayActivityName(activity.activityType)
        }));

        setActivities(activityData.map(act => ({
          ...act,
          date: new Date(toMillis(act.timestamp)).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
        })));
      } catch (error) {
        logger.error(error, { page: 'User', action: 'fetchActivities' });
        onError('Failed to fetch activities. Please try again.');
      } finally {
        setLoadingActivities(false);
      }
    };

    fetchActivities();
  }, [userId, onError]);

  // Effect #9: reset pagination to page 1 whenever the All Activities popup opens.
  useEffect(() => {
    if (showAllActivitiesPopup) {
      setCurrentPage(1);
    }
  }, [showAllActivitiesPopup]);

  // Effect #7: today-progress computation for the fuel widget. Moved here from
  // User.js because the fuel widget lives inside the activity-input card and
  // reads both `activities` (owned here) and `goal` + `currentWeight` (props).
  useEffect(() => {
    if (!goal || !activities.length) {
      setTodayProgress(0);
      return;
    }

    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);

    const todayActivities = activities.filter(
      activity => toMillis(activity.timestamp) >= todayStart.getTime() && toMillis(activity.timestamp) <= todayEnd.getTime()
    );

    let progress = 0;
    const goalType = goal.type?.toLowerCase();
    if (goalType === 'reps') {
      progress = todayActivities.reduce((sum, act) => sum + (act.count || 0), 0);
    } else if (goalType === 'minutes') {
      progress = todayActivities.reduce((sum, act) => sum + (act.time || 0), 0);
    } else if (goalType === 'calories') {
      progress = todayActivities.reduce((sum, act) => sum + calculateCaloriesBurned(act, currentWeight), 0);
    } else {
      logger.info('Unknown goal type:', goal.type);
    }
    setTodayProgress(Math.round(progress));
  }, [goal, activities, currentWeight]);

  // Lift today's total burned calories to parent (for EnergyBalance).
  // Runs independently of goal type — always sums calories.
  useEffect(() => {
    if (!onCaloriesBurned) return;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const burned = activities
      .filter(a => toMillis(a.timestamp) >= todayStart.getTime())
      .reduce((sum, act) => sum + calculateCaloriesBurned(act, currentWeight), 0);
    onCaloriesBurned(Math.round(burned));
  }, [activities, currentWeight, onCaloriesBurned]);

  const handleActivitySubmit = async (e) => {
    e.preventDefault();
    const validationError = validateActivity(newActivity);
    if (validationError) {
      onError(validationError);
      return;
    }
    if (!userId) {
      onError('User ID not found. Please try logging out and back in.');
      return;
    }
    const fields = activityFieldTypes[displayActivityName(newActivity.type)] || ['time'];
    const nowMs = Date.now();
    const activity = {
      activityType: displayActivityName(newActivity.type),
      count: newActivity.reps ? parseInt(newActivity.reps) || 0 : 0,
      time: fields.includes('time') ? parseInt(newActivity.time) || 0 : 0,
      weightLifted: fields.includes('weightLifted') ? parseInt(newActivity.weightLifted) || 0 : 0,
    };
    activity.caloriesBurned = calculateCaloriesBurned(activity, currentWeight);
    try {
      const docRef = await addDoc(collection(db, 'users', userId, 'reps'), {
        ...activity,
        timestamp: serverTimestamp(),
      });
      setActivities(prev => [{
        id: docRef.id,
        ...activity,
        timestamp: nowMs,
        date: new Date(nowMs).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
      }, ...prev]);
      setNewActivity({ type: '', reps: '', time: '', weightLifted: '' });
      onActivityLogged?.();
    } catch (error) {
      logger.error(error, { page: 'User', action: 'logActivity' });
      onError('Failed to log activity. Please try again.');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editActivity) return;
    const validationError = validateActivity(editActivity);
    if (validationError) {
      onError(validationError);
      return;
    }
    try {
      const activityRef = doc(db, 'users', userId, 'reps', selectedActivity.id);
      await updateDoc(activityRef, {
        activityType: displayActivityName(editActivity.type),
        count: parseInt(editActivity.reps) || 0,
        time: parseInt(editActivity.time) || 0,
        weightLifted: parseInt(editActivity.weightLifted) || 0
      });
      setActivities(activities.map(a =>
        a.id === selectedActivity.id ? {
          ...a,
          activityType: displayActivityName(editActivity.type),
          count: parseInt(editActivity.reps) || 0,
          time: parseInt(editActivity.time) || 0,
          weightLifted: parseInt(editActivity.weightLifted) || 0
        } : a
      ));
      setSelectedActivity(null);
      setEditActivity(null);
    } catch (error) {
      logger.error(error, { page: 'User', action: 'editActivity' });
      onError('Failed to update activity. Please try again.');
    }
  };

  const handleDelete = async () => {
    try {
      const activityRef = doc(db, 'users', userId, 'reps', selectedActivity.id);
      await deleteDoc(activityRef);
      setActivities(activities.filter(a => a.id !== selectedActivity.id));
      setSelectedActivity(null);
      onActivityLogged?.();
    } catch (error) {
      logger.error(error, { page: 'User', action: 'deleteActivity' });
      onError('Failed to delete activity. Please try again.');
    }
  };

  const handleGoalSave = async () => {
    const targetValue = parseInt(editGoal.target);
    if (isNaN(targetValue) || targetValue <= 0) {
      onError('Target must be a positive number.');
      return;
    }
    try {
      await setDoc(doc(db, 'users', userId, 'preferences', 'goals'),
        { target: targetValue.toString(), type: editGoal.type },
        { merge: true }
      );
      onGoalUpdate?.({ target: targetValue.toString(), type: editGoal.type });
      setShowGoalEditor(false);
    } catch (err) {
      logger.error(err, { page: 'User', action: 'saveGoalInline' });
      onError('Failed to save goal. Please try again.');
    }
  };

  return (
    <>
      <div className="activity-input">
        <h3>Log Activity</h3>
        <p className="motivational-quote">&ldquo;No matter how slow you go, you&rsquo;re still lapping everybody on the couch.&rdquo;</p>
        <div className="location-toggle">
          <button
            type="button"
            className={`location-toggle-btn${activityLocation === 'home' ? ' active' : ''}`}
            onClick={() => handleLocationToggle('home')}
          >
            Home
          </button>
          <button
            type="button"
            className={`location-toggle-btn${activityLocation === 'gym' ? ' active' : ''}`}
            onClick={() => handleLocationToggle('gym')}
          >
            Gym
          </button>
        </div>
        <form ref={formRef} onSubmit={handleActivitySubmit}>
          <select
            value={newActivity.type}
            onChange={(e) => setNewActivity({ ...newActivity, type: e.target.value })}
          >
            <option value="">Select Activity</option>
            {filteredActivities.map(activity => (
              <option key={activity} value={activity}>{displayActivityName(activity)}</option>
            ))}
          </select>
          <input
            type="number"
            value={newActivity.reps}
            onChange={(e) => setNewActivity({ ...newActivity, reps: e.target.value })}
            placeholder="Reps"
          />
          <input
            type="number"
            value={newActivity.time}
            onChange={(e) => setNewActivity({ ...newActivity, time: e.target.value })}
            placeholder="Time (min)"
          />
          {activityFieldTypes[displayActivityName(newActivity.type)]?.includes('weightLifted') && (
            <input
              type="number"
              value={newActivity.weightLifted}
              onChange={(e) => setNewActivity({ ...newActivity, weightLifted: e.target.value })}
              placeholder="Weight Lifted (lbs)"
            />
          )}
          <button type="submit" className="btn-primary">Log</button>
        </form>
        {availableActivities.length === 0 && (
          <p style={{ color: '#94a3b8', fontSize: '0.85em', margin: '8px 0' }}>
            No activities set up yet. Visit your <Link to="/account" style={{ color: '#f97316' }}>Account</Link> page to choose which activities to track.
          </p>
        )}
        <div className={`streak-widget${currentStreak < 2 ? ' streak-widget--inactive' : ''}`}>
          <div className="streak-primary">
            {currentStreak >= 2
              ? `🔥 ${currentStreak}-day streak`
              : '🔥 Start your streak today!'}
          </div>
          {longestStreak > 0 && (
            <div className="streak-best" style={{ color: '#94a3b8' }}>
              Best: {longestStreak} {longestStreak === 1 ? 'day' : 'days'}
            </div>
          )}
        </div>
        <div className="fuel-widget">
          {goal && goal.target ? (
            <div className="fuel-content">
              <span className="fuel-title">Find your fuel. Own it daily.</span>
              <div className="progress-container">
                <progress value={todayProgress} max={goal.target}></progress>
                <span className="progress-text" style={{ whiteSpace: 'nowrap' }}>
                  {todayProgress}/{goal.target} {goal.type}
                  <button
                    type="button"
                    onClick={() => { setEditGoal({ target: goal.target, type: goal.type }); setShowGoalEditor(true); }}
                    style={{ background: 'none', border: 'none', padding: 0, marginLeft: '4px', cursor: 'pointer', verticalAlign: 'middle', lineHeight: 0 }}
                    aria-label="Edit goal"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
                  </button>
                </span>
              </div>
              {goal.type?.toLowerCase() === 'calories' && (
                <div className="fuel-calorie-breakdown" style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.8)', marginTop: '4px' }}>
                  <span>Eaten: {caloriesConsumed}</span>
                  {' · '}
                  <span>Burned: {todayProgress}</span>
                  {' · '}
                  <span>Net: {caloriesConsumed - todayProgress}</span>
                </div>
              )}
            </div>
          ) : (
            <span className="no-fuel">
              No daily goal yet? Set yours in <Link to="/account">Account</Link>.
            </span>
          )}
        </div>
      </div>
      <div className="recent-activities">
        <div className="recent-activities-header">
          <h3>Recent Activities</h3>
          {loadingActivities && activities.length > 0 && (
            <span
              style={{
                position: 'absolute',
                right: '80px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '12px',
                fontStyle: 'italic',
                color: '#6c757d'
              }}
            >
              Refreshing...
            </span>
          )}
          <span
            className="show-all-link"
            onClick={() => setShowAllActivitiesPopup(true)}
          >
            Show All
          </span>
        </div>
        <p className="activity-instruction">Tap Show All to modify or delete.</p>
        {activities.length === 0 && loadingActivities ? (
          <p className="loading">Loading...</p>
        ) : activities.length === 0 ? (
          <div className="empty-state">
            <svg className="empty-state-icon" xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6.5 6.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/>
              <path d="M4 10l2-2 3 3 4-4 3 3"/>
              <path d="M20 17h-4l-2-3-3 5-2.5-2L6 20"/>
            </svg>
            <p className="empty-state-headline">No workouts logged yet</p>
            <p className="empty-state-subtitle">Select an activity above and hit Log to get started</p>
          </div>
        ) : (
          <div className="data-table data-table--activity">
            <div className="data-table-header">
              <span>Date</span>
              <span>Activity</span>
              <span>Details</span>
              <span>Calories <CaloriesInfoPopup currentWeight={currentWeight} calorieBurnRates={calorieBurnRates} /></span>
            </div>
            {activities
              .sort((a, b) => toMillis(b.timestamp) - toMillis(a.timestamp))
              .slice(0, 5)
              .map(activity => (
                <div
                  key={activity.id}
                  className="data-table-row"
                  onClick={() => {
                    setNewActivity({
                      type: displayActivityName(activity.activityType),
                      reps: activity.count ? activity.count.toString() : '',
                      time: activity.time ? activity.time.toString() : '',
                      weightLifted: activity.weightLifted ? activity.weightLifted.toString() : ''
                    });
                  }}
                >
                  <span className="data-table-cell" data-label="Date">{activity.date}</span>
                  <span className="data-table-cell" data-label="Activity">{displayActivityName(activity.activityType)}</span>
                  <span className="data-table-cell" data-label="Details">{formatActivityDetails(activity)}</span>
                  <span className="data-table-cell" data-label="Calories">{calculateCaloriesBurned(activity, currentWeight).toFixed(0)} cal</span>
                </div>
              ))}
          </div>
        )}
      </div>
      {selectedActivity && (
        <ModalBackdrop onClose={() => setSelectedActivity(null)}>
          <div className="popup-content">
            <h3>Activity Details</h3>
            <form onSubmit={handleEditSubmit}>
              <p className="activity-date">{new Date(toMillis(selectedActivity.timestamp)).toLocaleDateString()}</p>
              <label>
                Activity:
                <select
                  value={editActivity?.type || selectedActivity.activityType}
                  onChange={(e) => setEditActivity({ ...editActivity, type: e.target.value })}
                >
                  <option value="">Select Activity</option>
                  {[...new Set(availableActivities)].sort().map((activity) => (
                    <option key={activity} value={activity}>{displayActivityName(activity)}</option>
                  ))}
                </select>
              </label>
              <label>
                Reps:
                <input
                  type="number"
                  value={editActivity?.reps ?? selectedActivity.count}
                  onChange={(e) => setEditActivity({ ...editActivity, reps: e.target.value })}
                />
              </label>
              <label>
                Time (min):
                <input
                  type="number"
                  value={editActivity?.time ?? selectedActivity.time}
                  onChange={(e) => setEditActivity({ ...editActivity, time: e.target.value })}
                />
              </label>
              {activityFieldTypes[displayActivityName(editActivity?.type || selectedActivity.activityType)]?.includes('weightLifted') && (
                <label>
                  Weight Lifted (lbs):
                  <input
                    type="number"
                    value={editActivity?.weightLifted ?? selectedActivity.weightLifted}
                    onChange={(e) => setEditActivity({ ...editActivity, weightLifted: e.target.value })}
                  />
                </label>
              )}
              <div className="button-group">
                <button type="submit" className="btn-primary">Save</button>
                <button type="button" className="btn-danger" onClick={handleDelete}>Delete</button>
                <button type="button" className="btn-secondary" onClick={() => setSelectedActivity(null)}>Close</button>
              </div>
            </form>
          </div>
        </ModalBackdrop>
      )}
      {showAllActivitiesPopup && (
        <ModalBackdrop onClose={() => setShowAllActivitiesPopup(false)}>
          <div className="popup-content all-activities-popup">
            <div className="popup-header">
              <h3>All Activities</h3>
              <button
                className="popup-close-button btn-secondary"
                onClick={() => setShowAllActivitiesPopup(false)}
              >
                Close
              </button>
            </div>
            <p className="activity-instruction">Select an activity to modify or delete.</p>
            {activities.length === 0 ? (
              <p className="no-data">No activities yet.</p>
            ) : (
              <>
                <div className="data-table data-table--activity">
                  <div className="data-table-header">
                    <span>Date</span><span>Activity</span><span>Details</span><span>Calories</span>
                  </div>
                  {activities
                    .sort((a, b) => toMillis(b.timestamp) - toMillis(a.timestamp))
                    .slice((currentPage - 1) * 50, currentPage * 50)
                    .map((activity, i, arr) => {
                      const showDivider = i === 0 || getDateKey(activity.timestamp) !== getDateKey(arr[i - 1].timestamp);
                      return (
                        <React.Fragment key={activity.id}>
                          {showDivider && <div className="date-divider"><div className="date-divider-line" /><span>{getDateLabel(activity.timestamp)}</span><div className="date-divider-line" /></div>}
                          <div
                            className="data-table-row"
                            onClick={() => {
                              setSelectedActivity(activity);
                              const normalizedType = displayActivityName(activity.activityType);
                              setEditActivity({
                                type: normalizedType,
                                reps: activity.count,
                                time: activity.time,
                                weightLifted: activity.weightLifted
                              });
                            }}
                          >
                            <span className="data-table-cell" data-label="Date">{activity.date}</span>
                            <span className="data-table-cell" data-label="Activity">{displayActivityName(activity.activityType)}</span>
                            <span className="data-table-cell" data-label="Details">{formatActivityDetails(activity)}</span>
                            <span className="data-table-cell" data-label="Calories">{calculateCaloriesBurned(activity, currentWeight).toFixed(0)} cal</span>
                          </div>
                        </React.Fragment>
                      );
                    })}
                </div>
                {activities.length > 50 && (
                  <div className="pagination">
                    <button
                      className="btn-secondary"
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <span>Page {currentPage} of {Math.ceil(activities.length / 50)}</span>
                    <button
                      className="btn-secondary"
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(activities.length / 50)))}
                      disabled={currentPage === Math.ceil(activities.length / 50)}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </ModalBackdrop>
      )}
      {showGoalEditor && (
        <ModalBackdrop onClose={() => setShowGoalEditor(false)}>
          <div className="popup-content">
            <h3>Edit Daily Goal</h3>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px', color: '#f1f5f9', fontSize: '14px' }}>
              Target:
              <input
                type="number"
                value={editGoal.target}
                onChange={(e) => setEditGoal({ ...editGoal, target: e.target.value })}
                style={{ padding: '8px', borderRadius: '8px', border: '1px solid #334155', background: '#0f172a', color: '#f1f5f9', fontSize: '16px' }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '20px', color: '#f1f5f9', fontSize: '14px' }}>
              Type:
              <select
                value={editGoal.type}
                onChange={(e) => setEditGoal({ ...editGoal, type: e.target.value })}
                style={{ padding: '8px', borderRadius: '8px', border: '1px solid #334155', background: '#0f172a', color: '#f1f5f9', fontSize: '16px' }}
              >
                <option value="reps">Reps</option>
                <option value="minutes">Minutes</option>
                <option value="calories">Calories</option>
              </select>
            </label>
            <div className="button-group">
              <button className="btn-primary" onClick={handleGoalSave}>Save</button>
              <button className="btn-secondary" onClick={() => setShowGoalEditor(false)}>Cancel</button>
            </div>
          </div>
        </ModalBackdrop>
      )}
    </>
  );
}

export default ActivityLog;
